import { useState, useEffect } from 'react';
import { Box, Button, Grid, InputLabel, OutlinedInput, Typography, Divider, Switch, FormControl, Select, MenuItem } from '@mui/material';
import { toast } from 'react-toastify';
import { gridSpacing } from 'utils/constants';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import { validateNumber } from 'utils/commonHelpers';
import ApiHelper from 'utils/apiHelper';

const SecuritySettings = () => {
    const [securitySettingsData, setSecuritySettingsData] = useState({
        passwordSettings: {
            active2Factor: false,
            sso: false,
            attemptsLimit: 0,
            expirationLimit: 0,
            id: 0,
            pwdLength: 0,
            requireCapitalLetter: false,
            requireLowercase: false,
            requireNum: false,
            requireSpecialCharacter: false,
            reuseCycle: 0,
            specialCharacters: ''
        },
        autoUpgrade: false,
        current_versions: {
            windows: '',
            macOS: '',
            chromebook: '',
            bert: ''
        },
        selected_versions: {
            windows: '',
            macOS: '',
            chromebook: '',
            bert: ''
        },
        available_versions: {
            windows: [],
            macOS: [],
            chromebook: [],
            bert: []
        }
    });
    useEffect(() => {
        getSecuritySettingsData();
    }, []);

    const getSecuritySettingsData = () => {
        ApiHelper.get('/api/settings/security_settings/')
            .then((res) => {
                setSecuritySettingsData(res.data);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error(err.response.data.message);
                }
            });
    };

    const handleChangePasswordSettingsData = (e) => {
        if (e.target.value === '') toast.error('Please type the value');
        let type;
        if (
            e.target.name === 'pwdLength' ||
            e.target.name === 'attemptsLimit' ||
            e.target.name === 'expirationLimit' ||
            e.target.name === 'reuseCycle'
        ) {
            type = 'integer';
        } else {
            type = 'string';
        }
        if (validateNumber(e.target.value, type) === true) {
            setSecuritySettingsData({
                ...securitySettingsData,
                [e.target.id]: {
                    ...securitySettingsData[e.target.id],
                    [e.target.name]: e.target.value
                }
            });
        } else {
            if (type === 'integer') toast.error('It should be an integer value.');
            else toast.error('It should be a number');
        }
    };

    const handleChangeSelectedVersion = (e) => {
        setSecuritySettingsData({
            ...securitySettingsData,
            selected_versions: {
                ...securitySettingsData['selected_versions'],
                [e.target.name]: e.target.value
            }
        });
    };

    const handleChangeSwitchData = (e) => {
        setSecuritySettingsData({
            ...securitySettingsData,
            [e.target.id]: {
                ...securitySettingsData[e.target.id],
                [e.target.name]: e.target.checked
            }
        });
    };

    const handleSubmit = () => {
        if (
            securitySettingsData.passwordSettings.pwdLength === '' ||
            securitySettingsData.passwordSettings.attemptsLimit === '' ||
            securitySettingsData.passwordSettings.expirationLimit === '' ||
            securitySettingsData.passwordSettings.reuseCycle === ''
        ) {
            toast.error('There are some empty values');
            return true;
        }
        ApiHelper.put(`/api/settings/security_settings/${securitySettingsData.passwordSettings.id}/`, {
            pwd_length: securitySettingsData.passwordSettings.pwdLength,
            pwd_lockout: securitySettingsData.passwordSettings.attemptsLimit,
            pwd_expire: securitySettingsData.passwordSettings.expirationLimit,
            pwd_history: securitySettingsData.passwordSettings.reuseCycle,
            active_two_factor: securitySettingsData.passwordSettings.active2Factor,
            sso: securitySettingsData.passwordSettings.sso,
            pwd_cap_req: securitySettingsData.passwordSettings.requireCapitalLetter,
            pwd_lwr_req: securitySettingsData.passwordSettings.requireLowercase,
            pwd_num_req: securitySettingsData.passwordSettings.requireNum,
            pwd_spec_req: securitySettingsData.passwordSettings.requireSpecialCharacter,
            spec_char: securitySettingsData.passwordSettings.specialCharacters,
            auto_upgrade: securitySettingsData.autoUpgrade,
            selected_version_windows: securitySettingsData.selected_versions.windows,
            selected_version_macOS: securitySettingsData.selected_versions.macOS,
            selected_version_chromebook: securitySettingsData.selected_versions.chromebook,
            selected_version_bert: securitySettingsData.selected_versions.bert
        })
            .then(() => {
                toast.success('Successfully updated');
            })
            .catch(() => {
                toast.error('Failed to update the security settings data');
            });
    };

    if (!securitySettingsData) {
        return null;
    } else {
        return (
            <MainCard title="Security Settings" cardHeaderTitle="System Options" cardHeaderShow={true}>
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12} sx={{ position: 'relative', mt: 2, mb: 2 }}>
                        <Box sx={{ position: 'absolute', right: '0', top: '0' }}>
                            <Button size="large" color="secondary" variant="contained" onClick={handleSubmit}>
                                Save
                            </Button>
                            <Button onClick={getSecuritySettingsData}>Cancel</Button>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <SubCard title="Password Complexity Settings">
                            <Grid container>
                                <Grid item xs={6} sx={{ padding: '0 5px' }}>
                                    <InputLabel>Minimum Length (Chars) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        id="passwordSettings"
                                        value={securitySettingsData.passwordSettings.pwdLength}
                                        name="pwdLength"
                                        onChange={handleChangePasswordSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={6} sx={{ padding: '0 5px' }}>
                                    <InputLabel>Missed Attempts Limit (Trys) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        id="passwordSettings"
                                        value={securitySettingsData.passwordSettings.attemptsLimit}
                                        name="attemptsLimit"
                                        onChange={handleChangePasswordSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={6} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Expiration Limit (Days) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        id="passwordSettings"
                                        value={securitySettingsData.passwordSettings.expirationLimit}
                                        name="expirationLimit"
                                        onChange={handleChangePasswordSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={6} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Reuse Cycle (Times) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        id="passwordSettings"
                                        value={securitySettingsData.passwordSettings.reuseCycle}
                                        name="reuseCycle"
                                        onChange={handleChangePasswordSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={6} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Active 2-Factor Authentication</InputLabel>
                                    <Switch
                                        color="success"
                                        id="passwordSettings"
                                        checked={securitySettingsData.passwordSettings.active2Factor}
                                        name="active2Factor"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                <Grid item xs={6} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Require Capital Letter</InputLabel>
                                    <Switch
                                        color="success"
                                        id="passwordSettings"
                                        checked={securitySettingsData.passwordSettings.requireCapitalLetter}
                                        name="requireCapitalLetter"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                <Grid item xs={6} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Require Lowercase</InputLabel>
                                    <Switch
                                        color="success"
                                        id="passwordSettings"
                                        checked={securitySettingsData.passwordSettings.requireLowercase}
                                        name="requireLowercase"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                <Grid item xs={6} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Require Number</InputLabel>
                                    <Switch
                                        color="success"
                                        id="passwordSettings"
                                        checked={securitySettingsData.passwordSettings.requireNum}
                                        name="requireNum"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                <Grid item xs={6} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Require Special Character</InputLabel>
                                    <Switch
                                        color="success"
                                        id="passwordSettings"
                                        checked={securitySettingsData.passwordSettings.requireSpecialCharacter}
                                        name="requireSpecialCharacter"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                <Grid item xs={6} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Enable Single-Sign-On</InputLabel>
                                    <Switch
                                        color="success"
                                        id="passwordSettings"
                                        checked={securitySettingsData.passwordSettings.sso}
                                        name="sso"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                {securitySettingsData.passwordSettings.requireSpecialCharacter && (
                                    <Grid item xs={6} sx={{ padding: '0 5px', mt: 2 }}>
                                        <InputLabel>Special Characters</InputLabel>
                                        <OutlinedInput
                                            sx={{ width: '100%' }}
                                            id="passwordSettings"
                                            value={securitySettingsData.passwordSettings.specialCharacters}
                                            name="specialCharacters"
                                            onChange={handleChangePasswordSettingsData}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </SubCard>
                    </Grid>
                    <Grid item xs={6}>
                        <SubCard title="Device Upgrade Settings">
                            <Grid container>
                                <Grid item xs={4} sx={{ mt: 3 }}>
                                    <Typography>WakeSMART Client Upgrader</Typography>
                                </Grid>
                                <Grid item xs={8} sx={{ mt: 3 }}>
                                    <Grid container>
                                        <Grid item xs={12} sx={{ display: 'flex', textAlign: 'center', mb: 2 }}>
                                            <Typography sx={{ mt: 1 }}>Enable Auto-Upgrade</Typography>
                                            <Switch
                                                color="success"
                                                name="autoUpgrade"
                                                checked={securitySettingsData.autoUpgrade}
                                                onChange={(e) => {
                                                    setSecuritySettingsData({
                                                        ...securitySettingsData,
                                                        autoUpgrade: e.target.checked
                                                    });
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Typography sx={{ textAlign: 'center' }}>Windows</Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Typography sx={{ textAlign: 'center' }}>MacOS</Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Typography sx={{ textAlign: 'center' }}>Chrome</Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Typography sx={{ textAlign: 'center' }}>Bert</Typography>
                                        </Grid>
                                    </Grid>
                                    <Divider />
                                </Grid>
                                <Grid item xs={4} sx={{ mt: 3 }}>
                                    <Typography>Current Release Versions:</Typography>
                                </Grid>
                                <Grid item xs={8} sx={{ mt: 3 }}>
                                    <Grid container sx={{ textAlign: 'center' }}>
                                        <Grid item xs={3}>
                                            <Typography>
                                                {securitySettingsData.current_versions.windows === -1
                                                    ? 'No Installers'
                                                    : securitySettingsData.current_versions.windows}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Typography>
                                                {securitySettingsData.current_versions.macOS === -1
                                                    ? 'No Installers'
                                                    : securitySettingsData.current_versions.macOS}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Typography>
                                                {securitySettingsData.current_versions.chromebook === -1
                                                    ? 'No Installers'
                                                    : securitySettingsData.current_versions.chromebook}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Typography>
                                                {securitySettingsData.current_versions.bert === -1
                                                    ? 'No Installers'
                                                    : securitySettingsData.current_versions.bert}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={4} sx={{ mt: 3 }}>
                                    <Typography>Auto-Upgrade Version:</Typography>
                                </Grid>
                                <Grid item xs={8} sx={{ mt: 3 }}>
                                    <Grid container sx={{ textAlign: 'center' }}>
                                        <Grid item xs={3}>
                                            {securitySettingsData.available_versions.windows.length === 0 ? (
                                                <Typography>No Upgraders</Typography>
                                            ) : (
                                                <FormControl sx={{ width: '90%' }}>
                                                    <Select
                                                        value={securitySettingsData.selected_versions.windows}
                                                        displayEmpty
                                                        inputProps={{ 'aria-label': 'Without label' }}
                                                        name="windows"
                                                        onChange={handleChangeSelectedVersion}
                                                    >
                                                        {securitySettingsData.available_versions.windows.map((data, key) => (
                                                            <MenuItem value={data} key={key}>
                                                                {data}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            )}
                                        </Grid>
                                        <Grid item xs={3}>
                                            {securitySettingsData.available_versions.macOS.length === 0 ? (
                                                <Typography>No Upgraders</Typography>
                                            ) : (
                                                <FormControl sx={{ width: '90%' }}>
                                                    <Select
                                                        value={securitySettingsData.selected_versions.macOS}
                                                        displayEmpty
                                                        inputProps={{ 'aria-label': 'Without label' }}
                                                        name="macOS"
                                                        onChange={handleChangeSelectedVersion}
                                                    >
                                                        {securitySettingsData.available_versions.macOS.map((data, key) => (
                                                            <MenuItem value={data} key={key}>
                                                                {data}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            )}
                                        </Grid>
                                        <Grid item xs={3}>
                                            {securitySettingsData.available_versions.chromebook.length === 0 ? (
                                                <Typography>No Upgraders</Typography>
                                            ) : (
                                                <FormControl sx={{ width: '90%' }}>
                                                    <Select
                                                        value={securitySettingsData.selected_versions.chromebook}
                                                        displayEmpty
                                                        inputProps={{ 'aria-label': 'Without label' }}
                                                        name="chromebook"
                                                        onChange={handleChangeSelectedVersion}
                                                    >
                                                        {securitySettingsData.available_versions?.chromebook.map((data, key) => (
                                                            <MenuItem value={data} key={key}>
                                                                {data}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            )}
                                        </Grid>
                                        <Grid item xs={3}>
                                            {securitySettingsData.available_versions.bert.length === 0 ? (
                                                <Typography>No Upgraders</Typography>
                                            ) : (
                                                <FormControl sx={{ width: '90%' }}>
                                                    <Select
                                                        value={securitySettingsData.selected_versions.bert}
                                                        displayEmpty
                                                        inputProps={{ 'aria-label': 'Without label' }}
                                                        name="bert"
                                                        onChange={handleChangeSelectedVersion}
                                                    >
                                                        {securitySettingsData.available_versions.bert.map((data, key) => (
                                                            <MenuItem value={data} key={key}>
                                                                {data}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>
                </Grid>
            </MainCard>
        );
    }
};

export default SecuritySettings;
